/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions,no-unused-expressions */
import React, {FC, useCallback, useRef, useState} from 'react';
import {Key} from 'ts-key-enum';

import {
  FrontChangeEvent,
  FrontKeyboardEvent,
  HandlerOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {MeridiemsEnum, padStartWithZero, safeParseNumber, TimeAmPm} from '../../ruleTimeHelpers';
import {RulesGenericInput, StyledDigitsInput, StyledNumberLabel} from './genericInput';

/*
 * Types.
 */

interface InputTimeAMPMProps {
  time?: TimeAmPm;
  isDisabled?: boolean;
  setTime: HandlerOf<TimeAmPm>;
}

/*
 * Constants.
 */

const HHUpper = 13; // Upper bound (excluded) for hours.
const MMUpper = 60; // Upper bound (excluded) for minutes.

/*
 * Component.
 */

// @RULES TODO: consider refactoring this component
export const InputTimeAMPM: FC<InputTimeAMPMProps> = (props) => {
  const {time, setTime, isDisabled} = props;

  const hhRef = useRef<HTMLInputElement>(null);
  const mmRef = useRef<HTMLInputElement>(null);

  const [hh, setHH] = useState<string>(time?.hh !== undefined ? padStartWithZero(time.hh) : '');
  const [mm, setMM] = useState<string>(time?.mm !== undefined ? padStartWithZero(time.mm) : '');
  const meridiem = time?.meridiem || MeridiemsEnum.PM;

  const onChangeHH = useCallback(
    (evt: FrontChangeEvent<HTMLInputElement>) => {
      const {value} = evt.target;

      if (!value) {
        setHH('');
        return;
      }

      const maybeNumber = safeParseNumber(value);
      if (maybeNumber !== undefined && maybeNumber < HHUpper) {
        // We switch to MM when the previous value was single digit and the current one is double.
        const shouldSwitchToMM = value.length >= 2;
        setHH(value);

        // When HH input is complete, set the focus to the MM input.
        if (shouldSwitchToMM) {
          mmRef.current?.focus();
        }
      }
    },
    [setHH],
  );

  const onChangeMM = useCallback(
    (evt: FrontChangeEvent<HTMLInputElement>) => {
      const {value} = evt.target;

      if (!value) {
        setMM('');
        return;
      }

      const maybeNumber = safeParseNumber(value);
      if (maybeNumber !== undefined && maybeNumber < MMUpper) {
        setMM(value);
      }
    },
    [setMM],
  );

  // Switch back to HH if we press 'delete' on an empty MM.
  const onMMKeyDown = useCallback(
    (evt: FrontKeyboardEvent<HTMLInputElement>) => {
      if (mm === '' && (evt.key === Key.Delete || evt.key === Key.Backspace)) {
        hhRef.current?.focus();
      }
    },
    [mm],
  );

  // Only externally update HHMM's value on blur.
  const setBlur = useCallback(() => {
    const numberHH = Number.parseInt(hhRef.current?.value || '', 10);
    // On any blur, automatically populate the minutes with '0' if not present.
    const numberMM = Number.parseInt(mmRef.current?.value || '0', 10);

    // Cosmetic update : update numbers to be 2-digits on blur.
    if (numberHH < 10 && hh.length < 2) {
      setHH(padStartWithZero(numberHH));
    }

    if (numberMM < 10 && mm.length < 2) {
      setMM(padStartWithZero(numberMM));
    }

    setTime({
      hh: Number.isNaN(numberHH) ? undefined : numberHH,
      mm: Number.isNaN(numberMM) ? undefined : numberMM,
      meridiem,
    });
  }, [meridiem, setTime, hh, mm]);

  const toggleMeridiem = useCallback(() => {
    const newHH = Number.parseInt(hh, 10);
    const newMM =
      mm === ''
        ? 0 // Just like on HH change, default to 0 minutes if none are set.
        : Number.parseInt(mm, 10);

    setMM(padStartWithZero(newMM));

    const newMeridiem = meridiem === MeridiemsEnum.PM ? MeridiemsEnum.AM : MeridiemsEnum.PM;
    setTime({
      hh: newHH,
      mm: newMM,
      meridiem: newMeridiem,
    });
  }, [setTime, hh, mm, meridiem]);

  return (
    <RulesGenericInput inputId="input-am-pm">
      <StyledDigitsInput
        type="number"
        inputMode="numeric"
        pattern="[0-9]{0,2}"
        placeholder="00"
        value={hh}
        onChange={onChangeHH}
        min={1}
        ref={hhRef}
        onBlur={setBlur}
        disabled={isDisabled}
      />
      :
      <StyledDigitsInput
        type="number"
        inputMode="numeric"
        pattern="[0-9]{0,2}"
        placeholder="00"
        value={mm}
        onChange={onChangeMM}
        onKeyDown={onMMKeyDown}
        min={0}
        max={59}
        ref={mmRef}
        onBlur={setBlur}
        disabled={isDisabled}
      />
      <ChromelessButton onClick={toggleMeridiem} isDisabled={isDisabled}>
        <StyledNumberLabel>{meridiem}</StyledNumberLabel>
      </ChromelessButton>
    </RulesGenericInput>
  );
};
